/**
 * Fuente ÚNICA de verdad de la fecha de cobro de una factura SII emitida.
 * (ARQUITECTURA 7.18 / reglas 10.66, 10.68, 10.69.)
 *
 * Reemplazará —en pasos posteriores, de a uno con su impacto— todas las lecturas crudas
 * dispersas de `facturafechacobro`. En este archivo solo viven las funciones puras; NO está
 * cableado a ningún consumidor todavía.
 *
 * Definición de negocio: una factura está cobrada cuando el cliente efectivamente pagó, por
 * cualquiera de tres caminos, evaluados EN ORDEN (MBF → factoring → cuotas):
 *   1. MBF  — Σ de `movimiento_bancario_facturas.monto` (movimientos de Clay, tabla inmutable
 *             `movimientos_bancarios`) ≥ `monto_total` (tolerancia −0.01). Lógica de
 *             `getFacturasYaVinculadas` Fuente 3.
 *   2. Factoring — la factura tiene `estado_factoring='liquidada'`. El factoring es CON RECURSO:
 *             el adelanto NO cuenta; hasta que el cliente le paga al factoring y la operación se
 *             liquida, el riesgo es nuestro y la factura NO está cobrada.
 *   3. Cuotas — todas las cuotas del plan están no-pendientes (Fuente 2 de getFacturasYaVinculadas).
 *
 * (El fallback histórico por FK directa `movimientos_bancarios.factura_sii_emitida_id` —"Camino 4"—
 * se RETIRÓ en B3 Fase A: el excedente de un factoring, linkeado 1:1 por FK, se leía como cobro
 * directo y volvía liquidable una venta cobrada por factoring. El backfill B1 ya llevó los cobros
 * directos legítimos al puente MBF; los de factoring se resuelven por el Camino 2. Ver historial.)
 *
 * Fecha de cobro real = la fecha del evento que cerró el cobro:
 *   1. MBF        → la fecha del movimiento de Clay que COMPLETÓ el monto (acumulando por fecha asc).
 *                   Se deriva del join MBF → movimientos_bancarios.fecha, NO del `facturafechacobro`
 *                   denormalizado.
 *   2. Factoring  → fecha de liquidación de la operación (fecha_pago_real ?? fecha_cobro_final ??
 *                   fecha_operacion), igual que factoring.js al liquidar.
 *   3. Cuotas     → la fecha de la última cuota cobrada.
 *
 * MODEL-AGNOSTIC: la cobranza se resuelve a NIVEL DE FACTURA SII. MBF no conoce el modelo de venta
 * (sales / sales_contratos / sales_btl / negocios_carteleria) — la cobertura se calcula sobre la
 * factura y su `monto_total`, uniforme para los 4 modelos. El conector factura↔venta (vía
 * `facturas_sii_vinculaciones`: venta_tipo/venta_ref_id/venta_ref_index) solo importa para la fecha
 * ESTIMADA, que resuelve `plazosCobroSII.js` por modelo.
 */

const TOLERANCIA = 0.01;

/**
 * Indexa de una vez los datos crudos que los consumidores ya cargan, para que getCobroReal sea
 * O(1)/O(k) por factura (sin queries por factura). Cada input replica lo que ya se trae hoy:
 *   - mbfRows: movimiento_bancario_facturas (factura_tipo, factura_id, monto, movimiento_bancario_id)
 *   - movimientosBancarios: movimientos_bancarios (id, fecha) — solo para datar la vía MBF
 *   - facturasEmitidas: facturas_sii_emitidas (id, monto_total, estado_factoring)
 *   - operacionesFactoring: operaciones_factoring (id, estado, fecha_pago_real, fecha_cobro_final,
 *       fecha_operacion, factura_sii_emitida_id)
 *   - operacionesFactoringFacturas: operaciones_factoring_facturas (operacion_factoring_id, factura_sii_emitida_id)
 *   - planCuotas: plan_cobros_facturas (factura_sii_emitida_id, estado, fecha_cobro_real, fecha_vencimiento)
 */
export const buildContextoCobro = ({
  mbfRows = [],
  movimientosBancarios = [],
  facturasEmitidas = [],
  operacionesFactoring = [],
  operacionesFactoringFacturas = [],
  planCuotas = [],
} = {}) => {
  const fechaMovimiento = new Map();
  for (const m of movimientosBancarios) fechaMovimiento.set(m.id, m.fecha ?? null);

  const mbfPorFactura = new Map(); // facturaId -> [{ monto, fecha }]
  for (const r of mbfRows) {
    if (r.factura_tipo !== "sii_emitida" || r.factura_id == null) continue;
    if (!mbfPorFactura.has(r.factura_id)) mbfPorFactura.set(r.factura_id, []);
    mbfPorFactura.get(r.factura_id).push({
      monto: Number(r.monto || 0),
      fecha: fechaMovimiento.get(r.movimiento_bancario_id) ?? null,
    });
  }

  const montoTotalPorFactura = new Map();
  const estadoFactoringPorFactura = new Map();
  for (const f of facturasEmitidas) {
    montoTotalPorFactura.set(f.id, Number(f.monto_total || 0));
    estadoFactoringPorFactura.set(f.id, f.estado_factoring ?? null);
  }

  // Operación de factoring por factura para datar la vía factoring (Camino 2). Se arma con el link
  // ESCALAR `operaciones_factoring.factura_sii_emitida_id` (link primario: una op ↔ una factura) Y el
  // puente `operaciones_factoring_facturas` (ops multi-factura). Sin el escalar, una factura factoreada
  // 1:1 quedaría sin operación → `via='factoring'` sin fecha. Si hay varias ops, gana la 'liquidada'
  // (es la que se datea con fecha_pago_real).
  const operacionPorId = new Map();
  for (const op of operacionesFactoring) operacionPorId.set(op.id, op);
  const operacionPorFactura = new Map();
  const setOperacion = (facturaId, op) => {
    if (facturaId == null || !op) return;
    const prev = operacionPorFactura.get(facturaId);
    if (!prev || op.estado === "liquidada") operacionPorFactura.set(facturaId, op);
  };
  for (const op of operacionesFactoring) setOperacion(op.factura_sii_emitida_id, op);
  for (const off of operacionesFactoringFacturas)
    setOperacion(off.factura_sii_emitida_id, operacionPorId.get(off.operacion_factoring_id));

  const cuotasPorFactura = new Map();
  for (const c of planCuotas) {
    if (!cuotasPorFactura.has(c.factura_sii_emitida_id)) cuotasPorFactura.set(c.factura_sii_emitida_id, []);
    cuotasPorFactura.get(c.factura_sii_emitida_id).push(c);
  }

  return {
    mbfPorFactura,
    montoTotalPorFactura,
    estadoFactoringPorFactura,
    operacionPorFactura,
    cuotasPorFactura,
  };
};

const fechaLiquidacion = (op) => op?.fecha_pago_real ?? op?.fecha_cobro_final ?? op?.fecha_operacion ?? null;

/**
 * Resuelve la cobranza real de una factura SII. Evalúa MBF → Factoring → Cuotas y devuelve el
 * primer camino que dé cobrada.
 * @param {number} facturaSiiId
 * @param {object} ctx  resultado de buildContextoCobro
 * @returns {{ cobrada: boolean, fecha: string|null, via: 'mbf'|'factoring'|'cuotas'|null }}
 */
export const getCobroReal = (facturaSiiId, ctx) => {
  const NO = { cobrada: false, fecha: null, via: null };
  if (facturaSiiId == null || !ctx) return NO;

  // Camino 1 — MBF: Σ monto de movimientos de Clay ≥ monto_total. La fecha que CIERRA el cobro es
  // la del movimiento en que el acumulado (ordenado por fecha asc) alcanza el total.
  const montoTotal = ctx.montoTotalPorFactura.get(facturaSiiId) || 0;
  const mbf = ctx.mbfPorFactura.get(facturaSiiId) || [];
  if (montoTotal > 0 && mbf.length > 0) {
    const orden = [...mbf].sort((a, b) => String(a.fecha || "9999").localeCompare(String(b.fecha || "9999")));
    let acum = 0;
    for (const m of orden) {
      acum += m.monto;
      if (acum >= montoTotal - TOLERANCIA) {
        return { cobrada: true, fecha: m.fecha ?? null, via: "mbf" };
      }
    }
  }

  // Camino 2 — Factoring liquidado (CON RECURSO: solo 'liquidada').
  if (ctx.estadoFactoringPorFactura.get(facturaSiiId) === "liquidada") {
    return { cobrada: true, fecha: fechaLiquidacion(ctx.operacionPorFactura.get(facturaSiiId)), via: "factoring" };
  }

  // Camino 3 — Cuotas: todas no-pendientes → fecha de la última cuota cobrada.
  const cuotas = ctx.cuotasPorFactura.get(facturaSiiId) || [];
  if (cuotas.length > 0 && cuotas.every((c) => c.estado !== "pendiente")) {
    const fechas = cuotas
      .map((c) => c.fecha_cobro_real ?? c.fecha_vencimiento ?? null)
      .filter(Boolean)
      .sort((a, b) => String(a).localeCompare(String(b)));
    return { cobrada: true, fecha: fechas.length > 0 ? fechas.at(-1) : null, via: "cuotas" };
  }

  return NO;
};

/**
 * Mapa de cobro POR VENTA (no por factura SII), para consumidores que trabajan a nivel venta
 * (motor de comisión de edificios: sales / sales_contratos por mes). Recorre las vinculaciones
 * factura↔venta (`facturas_sii_vinculaciones`: venta_tipo/venta_ref_id/venta_ref_index → factura
 * SII) y resuelve getCobroReal de cada factura, keyando por `${venta_tipo}:${venta_ref_id}:${venta_ref_index??""}`.
 * Si una venta tiene varias facturas SII, queda cobrada si ALGUNA lo está (fecha más reciente).
 * @param {Array} vinculaciones  filas de facturas_sii_vinculaciones.
 * @param {object} ctx           resultado de buildContextoCobro.
 * @returns {Map<string, {cobrada, fecha, via}>}
 */
export const buildCobroPorVenta = (vinculaciones, ctx) => {
  const map = new Map();
  for (const v of vinculaciones || []) {
    if (v.venta_tipo == null || v.venta_ref_id == null) continue;
    const key = `${v.venta_tipo}:${v.venta_ref_id}:${v.venta_ref_index ?? ""}`;
    const cobro = getCobroReal(v.factura_sii_emitida_id, ctx);
    const prev = map.get(key);
    if (!prev) {
      map.set(key, cobro);
    } else if (cobro.cobrada && (!prev.cobrada || String(cobro.fecha) > String(prev.fecha))) {
      map.set(key, cobro);
    }
  }
  return map;
};

/**
 * Fecha de cobro ESTIMADA (proyectada). Precedencia: fecha_cobro_estimada (override manual) →
 * plazocobro → default (fecha base + 60 días, mirroring pipelineCobroSII). Recibe los campos YA
 * extraídos de la venta; la extracción por modelo (escalar para sales/btl, `meses[i]` para
 * contratos, folio para carteleria) la resuelve el caller vía `plazosCobroSII.js` —acá no se
 * duplica el recorrido de modelos, solo se aplica la precedencia.
 * @param {{ fecha_cobro_estimada?: string|null, plazocobro?: string|null, fecha_base?: string|null }} campos
 * @returns {string|null}
 */
export const getCobroEstimado = ({ fecha_cobro_estimada, plazocobro, fecha_base } = {}) => {
  if (fecha_cobro_estimada) return fecha_cobro_estimada;
  if (plazocobro) return plazocobro;
  if (fecha_base) {
    const d = new Date(`${String(fecha_base).slice(0, 10)}T00:00:00`);
    if (!Number.isNaN(d.getTime())) {
      d.setDate(d.getDate() + 60);
      return d.toISOString().split("T")[0];
    }
  }
  return null;
};
